import { useContext, useEffect } from 'react';
import { ApplicationRequestContext } from '../../ApplicationRequest';
import { Formik, Form, FieldArray, Field, FormikErrors } from 'formik';
import dayjs from 'dayjs';
import { BoxSection, TextM } from 'app/components';
import CheckboxComponent from 'app/components/Checkbox/Checkbox';
import { StyledBoxContainer, StyledContainer } from './styles';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

type PeriodItem = {
  credit_date: string;
  amount: number;
  selected: boolean;
  amount_to_use: number;
};

type FormValues = {
  periods: PeriodItem[];
};

const SimulationComponent = ({ backStep }: { backStep: () => void }) => {
  // Hooks
  const theme = useTheme();
  const { consignedList, setFgtsPeriods, setFgtsValidationErrors } = useContext(
    ApplicationRequestContext,
  );

  // Variables
  const periods = consignedList?.links[0]?.meta_data?.periods || [];

  // Handlers
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);

  const initialValues = {
    periods: periods
      .map((p) => {
        if (p.amount >= 50) {
          return {
            credit_date: p.credit_date,
            amount: p.amount,
            selected: true,
            amount_to_use: p.amount,
          };
        }
      })
      .filter((p) => p !== undefined)
      .slice(0, 12) as PeriodItem[],
  };

  useEffect(() => {
    if (initialValues.periods.length === 0) {
      backStep();
    }
  }, []);

  return (
    <StyledContainer>
      <BoxSection>
        <Formik<FormValues>
          initialValues={initialValues}
          validateOnMount={true}
          validate={(values) => {
            const errors: FormikErrors<FormValues> = {};
            const periodErrors: FormikErrors<PeriodItem>[] = [];

            values.periods.forEach((p, i) => {
              const fieldError: FormikErrors<PeriodItem> = {};

              if (p.selected) {
                if (p.amount_to_use < 50) {
                  fieldError.amount_to_use = 'Valor mínimo de R$ 50,00.';
                } else if (p.amount_to_use > p.amount) {
                  fieldError.amount_to_use = 'Excede o saldo disponível.';
                }
              }

              if (Object.keys(fieldError).length > 0) {
                periodErrors[i] = fieldError;
              }
            });

            if (periodErrors.length > 0) {
              errors.periods = periodErrors;
            }

            return errors;
          }}
          onSubmit={(values) => {
            console.info(values);
          }}
        >
          {({ values, setFieldValue, errors }) => {
            useEffect(() => {
              const filtered = values.periods
                .filter((p) => p.selected)
                .map(({ amount_to_use, credit_date, amount }) => ({
                  amount: amount_to_use,
                  credit_date,
                  available: amount,
                }));
              setFgtsPeriods(filtered);

              const hasValidationErrors = Object.keys(errors).length > 0;
              setFgtsValidationErrors(hasValidationErrors);
            }, [values.periods, errors]);

            const totalAvailable = values.periods.reduce(
              (acc, p) => acc + p.amount,
              0,
            );
            const totalToUse = values.periods.reduce(
              (acc, p) => (p.selected ? acc + p.amount_to_use : acc),
              0,
            );

            const allSelected = values.periods.every((p) => p.selected);
            const someSelected = values.periods.some((p) => p.selected);

            const handleSelectAll = () => {
              values.periods.forEach((_, index) => {
                setFieldValue(`periods[${index}].selected`, !allSelected);
              });
            };

            return (
              <Form>
                <StyledBoxContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <CheckboxComponent
                            customcolor={
                              theme.palette.brand.primary.base ||
                              'var(--primary)'
                            }
                            checked={allSelected}
                            indeterminate={someSelected && !allSelected}
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell>
                          <TextM weight="Bold">Data de aniversário</TextM>
                        </TableCell>
                        <TableCell>
                          <TextM weight="Bold">Saldo disponível</TextM>
                        </TableCell>
                        <TableCell>
                          <TextM weight="Bold">Saldo a ser consumido</TextM>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <FieldArray name="periods">
                        {() =>
                          values.periods.map((period, index) => {
                            const isSelected = period.selected;
                            const hasError = (
                              errors.periods?.[
                                index
                              ] as FormikErrors<PeriodItem>
                            )?.amount_to_use;
                            const rawValue = period.amount_to_use;

                            return (
                              <TableRow
                                key={index}
                                sx={{
                                  backgroundColor: isSelected
                                    ? theme.palette.grey[100]
                                    : theme.palette.common.white,
                                }}
                              >
                                <TableCell padding="checkbox">
                                  <CheckboxComponent
                                    customcolor={
                                      theme.palette.brand.primary.base
                                    }
                                    checked={isSelected}
                                    onChange={(e) =>
                                      setFieldValue(
                                        `periods[${index}].selected`,
                                        e.target.checked,
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  <TextM
                                    style={{
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    {dayjs(period.credit_date).format(
                                      'DD/MM/YYYY',
                                    )}
                                  </TextM>
                                </TableCell>
                                <TableCell>
                                  <TextM
                                    style={{
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    {formatCurrency(period.amount)}
                                  </TextM>
                                </TableCell>
                                <TableCell>
                                  <Field
                                    name={`periods[${index}].amount_to_use`}
                                    style={{ maxWidth: '29rem' }}
                                  >
                                    {() => {
                                      const formatted =
                                        formatCurrency(rawValue);

                                      return isSelected ? (
                                        <TextField
                                          fullWidth
                                          value={formatted}
                                          error={!!hasError}
                                          helperText={hasError}
                                          onChange={(e) => {
                                            const cleaned =
                                              e.target.value.replace(
                                                /[^\d]/g,
                                                '',
                                              );
                                            const numeric =
                                              parseFloat(cleaned) / 100;
                                            setFieldValue(
                                              `periods[${index}].amount_to_use`,
                                              isNaN(numeric) ? 0 : numeric,
                                            );
                                          }}
                                          InputProps={{
                                            sx: {
                                              backgroundColor:
                                                theme.palette.common.white,
                                              borderRadius: '10px',
                                              padding: 'none',
                                              maxWidth: '29rem',
                                            },
                                          }}
                                        />
                                      ) : (
                                        <TextField
                                          fullWidth
                                          disabled
                                          value="Parcela não selecionada"
                                          InputProps={{
                                            sx: {
                                              backgroundColor:
                                                theme.palette.grey[200],
                                              color: theme.palette.grey[800],
                                              borderRadius: '10px',
                                              padding: 'none',
                                            },
                                          }}
                                        />
                                      );
                                    }}
                                  </Field>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        }
                      </FieldArray>

                      {/* Footer */}
                      <TableRow>
                        <TableCell
                          style={{ borderBottom: 'none' }}
                          padding="checkbox"
                        />
                        <TableCell style={{ borderBottom: 'none' }} />
                        <TableCell style={{ borderBottom: 'none' }}>
                          <Typography fontWeight="bold">
                            Saldo total disponível
                          </Typography>
                          <Typography>
                            {formatCurrency(totalAvailable)}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ borderBottom: 'none' }}>
                          <Typography fontWeight="bold">
                            Saldo total a ser consumido
                          </Typography>
                          <Typography>{formatCurrency(totalToUse)}</Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </StyledBoxContainer>
              </Form>
            );
          }}
        </Formik>
      </BoxSection>
    </StyledContainer>
  );
};

export default SimulationComponent;
